'use client';

import { TIPOLOGIA_UTENZA_LABELS } from '@slims/shared/energy';
import { validatePod } from '@slims/shared/validation';
import { ChevronDown } from 'lucide-react';
import type { ComponentProps, ReactNode } from 'react';
import {
	DetailField as Field,
	DetailSelectInput as SelectInput,
	DetailTextInput as TextInput,
	DetailToggle as Toggle,
} from '../../molecules/detail-fields/detail-fields';
import {
	AppliancesEditor,
	type AppliancesEditorProps,
} from '../consumption-profile/appliances-editor';
import { ConsumptionProfileSection } from '../consumption-profile/consumption-profile-section';

export interface MemberMeterFields {
	codice: string;
	cabinaPrimaria: string;
	dso: string;
	verificatoGSE: boolean;
	nome: string;
	potenzaKwp: string;
	tecnologia: string;
	codiceGaudi: string;
	codiceUP: string;
	dataEntrataEsercizio: string;
	/**
	 * Derivato dalla data di entrata in esercizio (prima del 16/12/2021): si
	 * mostra, non si modifica (INB-328).
	 */
	preEsistente: boolean;
	/** Solo produzione: intensità del contributo PNRR in conto capitale (%). */
	pnrrGrantPct: string;
	/** Solo consumo: tipologia di cliente finale del POD (profili standard GSE). */
	tipologiaUtenza: string;
	/** Solo produzione: moduli e inverter, come li chiede la Guida SPC GSE. */
	numeroModuli: string;
	potenzaModuloW: string;
	potenzaInverterKw: string;
	/** Con più di un inverter la potenza è una somma e non si modifica da qui. */
	inverterCount: number;
}

const TIPOLOGIA_OPTIONS = Object.entries(TIPOLOGIA_UTENZA_LABELS).map(([value, label]) => ({
	value,
	label,
}));

/** Somma dei moduli in kWp, o `null` se manca uno dei due numeri. */
function moduliKwp(fields: MemberMeterFields): number | null {
	const n = Number(fields.numeroModuli);
	const w = Number(fields.potenzaModuloW);
	return fields.numeroModuli && fields.potenzaModuloW && n > 0 && w > 0 ? (n * w) / 1000 : null;
}

const kw = (v: number) => v.toLocaleString('it-IT', { maximumFractionDigits: 2 });

/**
 * Cosa dice la potenza inverter accanto ai moduli: il GSE prende la minore fra
 * la somma dei moduli e l'inverter (Guida SPC GSE).
 */
function inverterHint(fields: MemberMeterFields): string {
	if (fields.inverterCount > 1)
		return `Somma di ${fields.inverterCount} inverter: si modificano uno per uno dal monitoraggio.`;
	const moduli = moduliKwp(fields);
	const inv = Number(fields.potenzaInverterKw);
	if (moduli == null || !fields.potenzaInverterKw || !(inv > 0))
		return 'Potenza in uscita lato CA. Il GSE considera la minore fra questa e la somma dei moduli.';
	return `Potenza considerata dal GSE: ${kw(Math.min(moduli, inv))} kW, la minore fra moduli e inverter.`;
}

/** Campi d'impianto con un requisito RO (potenza, data di esercizio, contributo). */
export type MemberMeterPlantField = 'potenzaKwp' | 'dataEntrataEsercizio' | 'pnrrGrantPct';

/** Esito del controllo dei requisiti d'impianto su un campo (INB-322). */
export interface MemberMeterPlantIssue {
	livello: 'errore' | 'avviso';
	messaggio: string;
}

export interface MemberMeterViewProps {
	kind: 'M1' | 'M2';
	fields?: MemberMeterFields;
	onFieldChange: (field: keyof MemberMeterFields, value: string | boolean) => void;
	cabinaPlaceholder: string;
	cabinaError?: string;
	/**
	 * Requisiti d'impianto per campo: l'errore blocca il salvataggio, l'avviso
	 * dice che l'impianto non accede alla tariffa premio (INB-322).
	 */
	plantIssues?: Partial<Record<MemberMeterPlantField, MemberMeterPlantIssue>>;
	dsoOptions?: { value: string; label: string }[];
	declaredAnnualKwh?: string;
	onDeclaredAnnualKwhChange?: (value: string) => void;
	consumption?: ComponentProps<typeof ConsumptionProfileSection>;
	/** I dispositivi elettrici del socio (solo sul prelievo). */
	appliances?: AppliancesEditorProps;
	typicalProfile?: ReactNode;
	bill?: ReactNode;
	curve?: ReactNode;
	reader?: ReactNode;
	// Nessun pulsante di salvataggio qui: lo salva il «Salva» in alto del
	// dettaglio socio, insieme all'anagrafica (INB-375).
}

/** Un profilo, una bolletta, una curva. I dettagli si aprono nello stesso profilo. */
export function MemberMeterView({
	kind,
	fields,
	onFieldChange,
	cabinaPlaceholder,
	cabinaError,
	plantIssues = {},
	dsoOptions = [],
	declaredAnnualKwh,
	onDeclaredAnnualKwhChange,
	consumption,
	appliances,
	typicalProfile,
	bill,
	curve,
	reader,
}: MemberMeterViewProps) {
	const production = kind === 'M2';
	const issueProps = (campo: MemberMeterPlantField) => {
		const issue = plantIssues[campo];
		return issue?.livello === 'errore' ? { error: issue.messaggio } : { warning: issue?.messaggio };
	};
	return (
		<div className="space-y-5">
			<section
				aria-label={`Profilo ${kind}`}
				className="rounded-xl border border-border bg-card p-4 sm:p-5"
			>
				<h2 className="mb-4 text-base font-semibold text-text">
					Profilo di {production ? 'produzione' : 'consumo'}
				</h2>
				{!fields ? (
					<p className="mb-4 text-sm text-text-muted">
						Per attivare il punto {kind}, imposta il ruolo del socio a{' '}
						{production ? 'Produttore' : 'Consumatore'} o Prosumer in Anagrafica.
					</p>
				) : (
					<div className="space-y-4">
						<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
							{production && (
								<Field label="Nome impianto">
									<TextInput value={fields.nome} onChange={(v) => onFieldChange('nome', v)} />
								</Field>
							)}
							<Field label="Codice POD" error={validatePod(fields.codice)}>
								<TextInput
									value={fields.codice}
									placeholder="IT… dalla bolletta della luce"
									onChange={(v) => onFieldChange('codice', v)}
									uppercase
								/>
							</Field>
							{production ? (
								<>
									<Field label="Potenza impianto (kWp)" {...issueProps('potenzaKwp')}>
										<TextInput
											type="number"
											value={fields.potenzaKwp}
											onChange={(v) => onFieldChange('potenzaKwp', v)}
										/>
									</Field>
									<Field label="Tecnologia">
										<SelectInput
											value={fields.tecnologia}
											onChange={(v) => onFieldChange('tecnologia', v)}
											options={[
												{ value: 'fv', label: 'Fotovoltaico' },
												{ value: 'eolico', label: 'Eolico' },
												{ value: 'idro', label: 'Idroelettrico' },
												{ value: 'altro', label: 'Altro' },
											]}
										/>
									</Field>
									{/* In vista e non nei dettagli: decide se l'impianto accede alla tariffa
									    premio, ed è obbligatoria per salvarlo (INB-322). */}
									<Field label="Data entrata esercizio" {...issueProps('dataEntrataEsercizio')}>
										<TextInput
											type="date"
											value={fields.dataEntrataEsercizio}
											onChange={(v) => onFieldChange('dataEntrataEsercizio', v)}
										/>
									</Field>
									<Field label="Numero moduli">
										<TextInput
											type="number"
											value={fields.numeroModuli}
											onChange={(v) => onFieldChange('numeroModuli', v)}
										/>
									</Field>
									<Field
										label="Potenza del modulo (W)"
										hint={(() => {
											const tot = moduliKwp(fields);
											return tot == null
												? 'Dalla scheda tecnica del modulo, es. 400.'
												: `Somma dei moduli: ${kw(tot)} kWp.`;
										})()}
									>
										<TextInput
											type="number"
											value={fields.potenzaModuloW}
											onChange={(v) => onFieldChange('potenzaModuloW', v)}
										/>
									</Field>
									<Field label="Potenza inverter (kW)" hint={inverterHint(fields)}>
										<TextInput
											type="number"
											value={fields.potenzaInverterKw}
											onChange={(v) => onFieldChange('potenzaInverterKw', v)}
											disabled={fields.inverterCount > 1}
										/>
									</Field>
								</>
							) : (
								<>
									<Field label="Tipo di utenza">
										<SelectInput
											value={fields.tipologiaUtenza}
											onChange={(v) => onFieldChange('tipologiaUtenza', v)}
											options={TIPOLOGIA_OPTIONS}
											placeholder="- da indicare -"
										/>
									</Field>
									<Field label="Consumo annuo dichiarato (kWh/anno)">
										<TextInput
											type="number"
											value={declaredAnnualKwh ?? ''}
											placeholder="Es. 3500"
											onChange={(v) => onDeclaredAnnualKwhChange?.(v)}
										/>
									</Field>
								</>
							)}
						</div>
					</div>
				)}
				{appliances && (
					<div className="mt-4 space-y-3 border-t border-border pt-4">
						<h3 className="text-sm font-medium text-text">Dispositivi elettrici</h3>
						<AppliancesEditor {...appliances} />
					</div>
				)}
				<details className="group mt-4 border-t border-border pt-3">
					<summary className="flex cursor-pointer list-none items-center justify-between gap-3 rounded-md py-1 text-sm font-medium text-text focus-visible:outline-2 focus-visible:outline-accent [&::-webkit-details-marker]:hidden">
						Dettagli del profilo
						<ChevronDown
							className="size-4 shrink-0 transition-transform group-open:rotate-180"
							aria-hidden
						/>
					</summary>
					<div className="mt-4 space-y-5">
						{fields && (
							<>
								<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
									<Field label="Cabina primaria" error={cabinaError}>
										<TextInput
											value={fields.cabinaPrimaria}
											placeholder={cabinaPlaceholder}
											onChange={(v) => onFieldChange('cabinaPrimaria', v)}
											uppercase
										/>
									</Field>
									{!production && (
										<Field label="DSO (distributore)">
											<SelectInput
												value={fields.dso}
												onChange={(v) => onFieldChange('dso', v)}
												options={dsoOptions}
											/>
										</Field>
									)}
									{production && (
										<>
											<Field label="Codice GAUDÌ (Terna)">
												<TextInput
													value={fields.codiceGaudi}
													onChange={(v) => onFieldChange('codiceGaudi', v)}
													uppercase
												/>
											</Field>
											<Field label="Codice UP (GSE)">
												<TextInput
													value={fields.codiceUP}
													onChange={(v) => onFieldChange('codiceUP', v)}
													uppercase
												/>
											</Field>
											<Field
												label="Contributo in conto capitale, es. PNRR (%)"
												hint="Con un contributo in conto capitale la quota di energia condivisa incentivabile si ferma al 45% dell'energia immessa invece del 55% (D.M. 414/2023 e Regole Operative CACER). 0 se l'impianto non l'ha ricevuto."
												{...issueProps('pnrrGrantPct')}
											>
												<TextInput
													type="number"
													value={fields.pnrrGrantPct}
													onChange={(v) => onFieldChange('pnrrGrantPct', v)}
												/>
											</Field>
										</>
									)}
								</div>
								{production ? (
									<Toggle
										label="Impianto pre-esistente"
										hint="Dalla data di entrata in esercizio: prima del 16/12/2021. Per cambiarlo, correggi la data."
										checked={fields.preEsistente}
										onChange={() => {}}
										disabled
									/>
								) : (
									<Toggle
										label="Verificato GSE"
										checked={fields.verificatoGSE}
										onChange={() => onFieldChange('verificatoGSE', !fields.verificatoGSE)}
									/>
								)}
								{typicalProfile && (
									<div className="space-y-2 border-t border-border pt-4">
										<h3 className="text-sm font-medium text-text">Profilo GSE</h3>
										{typicalProfile}
									</div>
								)}
							</>
						)}
						{consumption && (
							<div className="space-y-3 border-t border-border pt-4">
								<h3 className="text-sm font-medium text-text">Caratteristiche di consumo</h3>
								<ConsumptionProfileSection {...consumption} />
							</div>
						)}
					</div>
				</details>
			</section>
			{bill}
			{curve}
			{reader}
		</div>
	);
}
